import { useEffect, useState } from "react";

import { useRouter } from "src/hooks/use-router";
import { sendVerifyEmailEmail } from "src/utils/snug/snugAuthApi";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import { readStarterPayContext } from "src/sections/starter-pay/context";
import {
  readCustomPlanParams,
  customPlanQueryString,
} from "src/sections/starter-pay/custom-plan-storage";
import "./starter-pay.css";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

const REDIRECT_BASE = "/get-started-payment";
const SENT_FLAG_PREFIX = "sp_verify_sent:";

// Append any stashed custom plan params so a customer who clicks the email
// link on a different device still gets the right pricing.
function buildRedirectPath(): string {
  return REDIRECT_BASE + customPlanQueryString(readCustomPlanParams());
}

function hasAlreadySent(email: string): boolean {
  try {
    return sessionStorage.getItem(SENT_FLAG_PREFIX + email) === "1";
  } catch {
    return false;
  }
}

function markSent(email: string): void {
  try {
    sessionStorage.setItem(SENT_FLAG_PREFIX + email, "1");
  } catch {
    // ignore storage errors
  }
}

export default function GetStartedVerifyPage() {
  const router = useRouter();
  const [email, setEmail] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const ctx = readStarterPayContext();
    if (!ctx?.email) {
      router.replace("/get-started-account");
      return;
    }
    const ctxEmail = ctx.email;
    setEmail(ctxEmail);
    if (hasAlreadySent(ctxEmail)) {
      setSent(true);
      return;
    }
    sendVerifyEmailEmail(ctxEmail, false, buildRedirectPath())
      .then(() => {
        markSent(ctxEmail);
        setSent(true);
      })
      .catch(() => setError("Could not send verification email. Please try again."));
  }, [router]);

  if (!isGetSnugDomain) return null;

  const handleResend = async () => {
    if (!email || resending) return;
    setResending(true);
    setError("");
    try {
      await sendVerifyEmailEmail(email, false, buildRedirectPath());
      markSent(email);
      setSent(true);
    } catch {
      setError("Could not resend verification email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started with <em>Snug.</em>
        </h1>

        <p className="sp-sub">
          Includes full platform access, white-label setup, and onboarding
          support.
        </p>

        <SignupStepper currentStep="account" />

        <div className="sp-card" style={{ textAlign: "center", padding: "2.5rem 2rem" }}>
          <img
            src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/inbox.png"
            alt=""
            style={{ maxWidth: 48, marginBottom: "1rem" }}
          />
          <h2 className="sp-section-title" style={{ marginBottom: "0.5rem" }}>
            Check your inbox
          </h2>
          <p className="sp-section-help">
            We sent a verification link to{" "}
            <strong>{email}</strong>.
            <br />
            Click the link in the email to continue to payment.
          </p>
          <p className="sp-section-help" style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
            Can't find it? Check your spam folder.
          </p>
          <button
          type="button"
          className="sp-cta"
          style={{ background: "transparent", color: "#3045C9", border: "1.5px solid #3045C9", marginTop: "1.5rem" }}
          disabled={resending}
          onClick={handleResend}
        >
          {resending ? <span className="sp-cta-spinner" aria-hidden="true" /> : null}
          {resending ? "Sending…" : sent ? "Resend verification email" : "Send verification email"}
        </button>
        </div>

        {error && <div className="sp-error">{error}</div>}

        

        <p className="sp-disclaimer">
          The link expires after 24 hours. If you have trouble, contact{" "}
          <a href="mailto:hello@getsnug.com">hello@getsnug.com</a>.
        </p>
      </section>
    </div>
  );
}
